// src/components/MapView/OpportunityMarkers.jsx
import React, { useEffect, useState } from "react";
import { CircleMarker, Tooltip, Popup, useMap } from "react-leaflet";
import { useSelector } from "react-redux";
import {
  selectActiveOpportunities,
  selectOpportunitiesLoading,
} from "../../store/dataSlice";
import { selectOpportunitiesVisible } from "../../store/mapSlice";
import { selectActiveGeographyType } from "../../store/configSlice";

/**
 * Custom pane for opportunity markers. z-index 510 sits above the
 * competitorsPane (500) and overlayPane (400, GeoJSON polygons) but
 * below markerPane (600) and tooltipPane (650).
 */
const OPPORTUNITIES_PANE = "opportunitiesPane";
const OPPORTUNITIES_PANE_Z = 510;

/**
 * Opportunity pin style: purple (#7B1FA2), distinct from all tier colors
 * and the teal competitor markers.
 */
const MARKER_OPTIONS = {
  radius: 6,
  fillColor: "#7B1FA2",
  fillOpacity: 0.9,
  color: "#ffffff",
  weight: 2,
  opacity: 1,
};

const MARKER_HOVER_OPTIONS = {
  radius: 8,
  fillColor: "#6A1B9A",
  fillOpacity: 1,
  color: "#ffffff",
  weight: 2,
  opacity: 1,
};

/**
 * Renders opportunity locations as circle markers on the Leaflet map.
 * Only renders when the opportunities toggle is active and data is loaded.
 *
 * Uses CircleMarker with Tooltip (hover) and Popup (click).
 * Renders in a custom pane above GeoJSON polygons and competitor markers.
 */
const OpportunityMarkers = () => {
  const map = useMap();
  const [paneReady, setPaneReady] = useState(false);

  const visible = useSelector(selectOpportunitiesVisible);
  const opportunities = useSelector(selectActiveOpportunities);
  const loading = useSelector(selectOpportunitiesLoading);
  const geographyType = useSelector(selectActiveGeographyType);

  // Create custom pane once
  useEffect(() => {
    if (map && !paneReady) {
      if (!map.getPane(OPPORTUNITIES_PANE)) {
        const pane = map.createPane(OPPORTUNITIES_PANE);
        pane.style.zIndex = OPPORTUNITIES_PANE_Z;
      }
      setPaneReady(true);
    }
  }, [map, paneReady]);

  if (!visible || !opportunities.length || !paneReady) return null;

  const geoField = geographyType === "BG" ? "blockGroup" : "zcta";
  const geoLabel = geographyType === "BG" ? "Block Group" : "ZIP Code";

  return (
    <>
      {opportunities.map((opp) => (
        <CircleMarker
          key={opp.placeId}
          center={[opp.lat, opp.lng]}
          pathOptions={{ ...MARKER_OPTIONS, pane: OPPORTUNITIES_PANE }}
          eventHandlers={{
            click: (e) => {
              e.target.closeTooltip();
            },
            mouseover: (e) => {
              e.target.setStyle(MARKER_HOVER_OPTIONS);
              e.target.setRadius(MARKER_HOVER_OPTIONS.radius);
            },
            mouseout: (e) => {
              e.target.setStyle(MARKER_OPTIONS);
              e.target.setRadius(MARKER_OPTIONS.radius);
            },
          }}
        >
          <Tooltip
            direction="top"
            offset={[0, -8]}
            className="opportunity-tooltip"
          >
            <div style={{ minWidth: 160, maxWidth: 260 }}>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: 13,
                  marginBottom: 1,
                  color: "#1a1a1a",
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {opp.name}
              </div>
              {opp.businessType && (
                <div
                  style={{
                    color: "#7B1FA2",
                    fontSize: 11,
                    fontWeight: 500,
                    marginBottom: 1,
                  }}
                >
                  {opp.businessType}
                </div>
              )}
              <div
                style={{
                  color: "#555",
                  fontSize: 11,
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {opp.address}
              </div>
              <div style={{ color: "#555", fontSize: 11 }}>{opp.city}</div>
              <div style={{ color: "#888", fontSize: 10, marginTop: 3 }}>
                {geoLabel}: {opp[geoField] || "\u2014"}
              </div>
              {opp.website && (
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 11,
                    wordWrap: "break-word",
                    overflowWrap: "break-word",
                    lineHeight: 1.3,
                    color: "#7B1FA2",
                  }}
                >
                  <a
                    href={opp.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "inherit", textDecoration: "underline" }}
                  >
                    {opp.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                  </a>
                </div>
              )}
            </div>
          </Tooltip>
          <Popup
            offset={[0, 0]}
            className="opportunity-popup"
            closeButton={true}
          >
            <div style={{ width: 240 }}>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: 13,
                  marginBottom: 1,
                  color: "#1a1a1a",
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {opp.name}
              </div>
              {opp.businessType && (
                <div
                  style={{
                    color: "#7B1FA2",
                    fontSize: 11,
                    fontWeight: 500,
                    marginBottom: 1,
                  }}
                >
                  {opp.businessType}
                </div>
              )}
              <div
                style={{
                  color: "#555",
                  fontSize: 11,
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {opp.address}
              </div>
              <div style={{ color: "#555", fontSize: 11 }}>{opp.city}</div>
              <div style={{ color: "#888", fontSize: 10, marginTop: 3 }}>
                {geoLabel}: {opp[geoField] || "\u2014"}
              </div>
              {opp.website && (
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 11,
                    wordWrap: "break-word",
                    overflowWrap: "break-word",
                    lineHeight: 1.3,
                    color: "#7B1FA2",
                  }}
                >
                  <a
                    href={opp.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "inherit", textDecoration: "underline" }}
                  >
                    {opp.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                  </a>
                </div>
              )}
            </div>
          </Popup>
        </CircleMarker>
      ))}
    </>
  );
};

export default OpportunityMarkers;
